import type { MyDayTask } from '@performance-crm/shared';
import { Bot, CalendarClock, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StageBadge } from '@/components/leads/badges';
import { appointmentDue } from '@/components/leads/appointment';
import type { AttendanceOutcome } from '@/components/leads/dialogs';
import { NoContactBadge, QuickContactButtons } from '@/components/leads/QuickContactButtons';
import { describeAppointment, fmt, TYPE_LABEL } from '@/lib/format';

export function TaskRow({
  task,
  tone,
  onComplete,
  onAttendance,
  onReschedule,
}: {
  task: MyDayTask;
  tone: 'danger' | 'default' | 'muted';
  onComplete: (t: MyDayTask) => void;
  /** E1: una cita se confirma (asistió / no asistió) por el mismo camino que en la ficha; no se «completa». */
  onAttendance: (t: MyDayTask, outcome: AttendanceOutcome | null) => void;
  onReschedule: (t: MyDayTask) => void;
}) {
  const isAppointment = task.type === 'SE_CITO';
  const due = appointmentDue(task.scheduledAt);
  const confirmations = task.appointment?.confirmations;
  return (
    <li
      className="bg-card flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center"
      data-testid="task-row"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/leads/${task.lead.id}`} className="font-medium hover:underline">
            {task.lead.contactName}
          </Link>
          <StageBadge stage={task.lead.pipelineStage} />
          {!task.lead.contactable && <NoContactBadge />}
          {task.isHandoff && (
            <Badge tone="primary">
              <Bot className="h-3 w-3" /> Handoff del bot
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground mt-0.5 text-sm">
          <span className={tone === 'danger' ? 'text-danger font-medium' : ''}>
            {TYPE_LABEL[task.type]} · {fmt.dateTimeFull(task.scheduledAt)}
          </span>
          {' · '}
          {task.lead.projectName}
        </p>
        {task.appointment && (
          <p className="mt-1 text-sm">
            {describeAppointment(task.appointment)}
            {task.appointment.url && (
              <>
                {' · '}
                <a
                  href={task.appointment.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  abrir enlace
                </a>
              </>
            )}
          </p>
        )}
        {isAppointment && confirmations && (
          <p className="text-muted-foreground mt-1 text-xs" data-testid="task-appointment-status">
            {task.appointment?.roomAdvisorName
              ? `Asesor de sala: ${task.appointment.roomAdvisorName} · `
              : ''}
            {confirmations.needsDouble
              ? `confirmó digital: ${confirmations.digital ? 'sí' : 'no'} · sala: ${confirmations.room ? 'sí' : 'no'}`
              : confirmations.digital
                ? 'confirmación registrada'
                : 'sin confirmar'}
            {!due ? ` · disponible desde el ${fmt.dateTimeFull(task.scheduledAt)}` : ''}
          </p>
        )}
        {task.description && <p className="mt-1 line-clamp-2 text-sm">{task.description}</p>}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1">
        <QuickContactButtons
          leadId={task.lead.id}
          phoneNormalized={task.lead.phoneNormalized}
          contactable={task.lead.contactable}
        />
        <Button
          variant="outline"
          size="icon"
          aria-label="Reprogramar"
          onClick={() => onReschedule(task)}
        >
          <CalendarClock className="h-4 w-4" />
        </Button>
        {isAppointment ? (
          <>
            <Button
              size="sm"
              onClick={() => onAttendance(task, null)}
              disabled={!due}
              title={due ? undefined : `Disponible desde el ${fmt.dateTimeFull(task.scheduledAt)}`}
              data-testid="btn-attendance"
            >
              <Check className="h-4 w-4" /> Confirmar asistencia
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAttendance(task, 'NO_ASISTIO')}
              disabled={!due}
              title={due ? undefined : `Disponible desde el ${fmt.dateTimeFull(task.scheduledAt)}`}
              data-testid="btn-no-show"
            >
              No asistió / reprogramar
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={() => onComplete(task)}>
            <Check className="h-4 w-4" /> Completar
          </Button>
        )}
      </div>
    </li>
  );
}
